export type SourceType = 'xlsx' | 'pdf' | 'email';

export interface FileSource {
  id:        string;
  label:     string;
  desc:      string;
  accept:    string;
  type:      SourceType;
  multi?:    boolean;
  /** Se true, la card non blocca il submit del modal (upload facoltativo). */
  optional?: boolean;
}

/** Mapping source.id (modal) -> document_type (BE enum ConductRiskDocumentType). */
export const SOURCE_ID_TO_DOC_TYPE: Record<string, string | undefined> = {
  riepilogo_email:                 'performance_summary_email', // Driver 01 + Driver 09 (stesso pool di mail)
  pvr_quota_a:                     'pvr_quota_a',
  // pvr_quota_optional accetta piu' file (Quota B, Quota C, ...). Il BE ha
  // unificato quota_b -> quota_optional (2026-07-14) come da rilascio dev.
  pvr_quota_optional:              'pvr_quota_optional',
  mintr_corrente:                  'mintr_current',
  mintr_precedente:                'mintr_prior',
  evoluzione_ricavi:               'evoluzione_ricavi',
  estratto_seiok:                  'sei_ok',
  icf:                             'icf',
  global_advisor:                  'global_advisor',
  dettaglio_scheda_direttori_area: 'dettaglio_scheda_direttori_area', // Driver 08 (sostituisce mapping_direzioni)
  risultati_direttori:             'risultati_direttori_area',
  regole_pvr:                      'regole_pvr', // Annex PVR (aggiunto 2026-07-14)
  // Snapshot Q3 (30/09) usati dai driver Investigation (10, 11). Facoltativi.
  pvr_quota_a_september:           'pvr_quota_a_september',
  pvr_quota_c_september:           'pvr_quota_c_september',
};

/** Inverso: dato un document_type ritorna i sourceId della modale che lo mappano. */
export const docTypeToSourceIds = (documentType: string): string[] =>
  Object.entries(SOURCE_ID_TO_DOC_TYPE)
    .filter(([, dt]) => dt === documentType)
    .map(([id]) => id);

/** Label leggibile di un document_type: ritorna la label del primo sourceId che
 *  lo mappa, oppure il documentType raw come fallback. */
export const docTypeLabel = (documentType: string): string => {
  const sourceIds = docTypeToSourceIds(documentType);
  const src = SOURCES.find(s => sourceIds.includes(s.id));
  return src?.label ?? documentType;
};

/** Accept (estensioni file) associato a un document_type. */
export const docTypeAccept = (documentType: string): string => {
  const sourceIds = docTypeToSourceIds(documentType);
  const src = SOURCES.find(s => sourceIds.includes(s.id));
  return src?.accept ?? '';
};

export const SOURCES: FileSource[] = [
  { id: 'riepilogo_email',      label: 'Email di Riepilogo (Reclami e Formazione)', desc: 'Carica le email con Performance Reclami e Fruizione Formazione (.html, .htm).', accept: '.html,.htm',  type: 'email', multi: true },
  { id: 'pvr_quota_a',          label: 'PVR Quota A',                    desc: 'Premio Variabile di Risultato Quota A',                                  accept: '.xlsx,.xls,.xlsb', type: 'xlsx' },
  { id: 'pvr_quota_optional',   label: 'PVR Quota Opzionale (B, C, ...)', desc: 'Quote opzionali del Premio Variabile di Risultato (Quota B, Quota C, ecc.)', accept: '.xlsx,.xls,.xlsb', type: 'xlsx', multi: true },
  { id: 'mintr_corrente',       label: 'MINTR Anno Corrente',            desc: 'Margine Intermediazione Lordo Gestionale',                               accept: '.xlsx,.xls,.xlsb', type: 'xlsx' },
  { id: 'mintr_precedente',     label: 'MINTR Anno Precedente',          desc: 'Margine Intermediazione Lordo Gestionale (OLD)',                         accept: '.xlsx,.xls,.xlsb', type: 'xlsx' },
  { id: 'evoluzione_ricavi',    label: 'Evoluzione Ricavi',              desc: 'Evoluzione trimestrale dei ricavi per filiale',                          accept: '.xlsx,.xls,.xlsb', type: 'xlsx' },
  { id: 'estratto_seiok',       label: 'ESTRATTO SEI OK',                desc: 'Estratto dall\'applicativo Sei OK',                                      accept: '.pdf',       type: 'pdf'  },
  { id: 'icf',                  label: 'ICF',                            desc: 'Indicatore di Conformità per filiale',                                   accept: '.xlsx,.xls,.xlsb', type: 'xlsx' },
  { id: 'global_advisor',       label: 'Global Advisor',                 desc: 'Dati gestori Global Advisor',                                            accept: '.xlsx,.xls,.xlsb', type: 'xlsx' },
  { id: 'dettaglio_scheda_direttori_area', label: 'Dettaglio Scheda Direttori d\'Area', desc: 'Dettaglio della scheda direttori d\'area per il report', accept: '.xlsx,.xls,.xlsb', type: 'xlsx' },
  { id: 'risultati_direttori',  label: 'Risultati Direttori d\'Area',    desc: 'Risultati Direttori d\'area per l\'anno',                                accept: '.xlsx,.xls,.xlsb', type: 'xlsx' },
  { id: 'regole_pvr',           label: 'Annex PVR',                      desc: 'Regole in materia di Premio Variabile di Risultato',                     accept: '.pdf',       type: 'pdf'  },
  // ─── Investigation (Q3 snapshot al 30/09, facoltativi) ─────────────────
  { id: 'pvr_quota_a_september', label: 'PVR Quota A (30/09 snapshot)',  desc: 'Snapshot Quota A al 30/09 — usato per Driver 10 (salto di fasce Q3→Q4)', accept: '.xlsx,.xls,.xlsb', type: 'xlsx', optional: true },
  { id: 'pvr_quota_c_september', label: 'PVR Quota C (30/09 snapshot)',  desc: 'Snapshot Quota C al 30/09 — usato per Driver 11 (incremento commissioni nette Q4)', accept: '.xlsx,.xls,.xlsb', type: 'xlsx', optional: true },
];
